import { ChangeDetectionStrategy, Component, inject, Input, OnChanges, SimpleChanges } from '@angular/core';
import { VariableBenefitConfigurationStepFormGroup } from '../../../../records/components/members/ip-view-member-record/components/ip-member-record-variable-benefit-configuration/form-groups/variable-benefit-configuration-step-form-group';
import { VariableBenefitRecurringPaymentFormGroup } from '../../../../records/components/members/ip-view-member-record/components/ip-member-record-variable-benefit-configuration/form-groups/variable-benefit-recurring-payment-form-group';
import { VariableBenefitPaymentStepFormGroup } from '../../../../records/components/members/ip-view-member-record/components/ip-member-record-variable-benefit-configuration/form-groups/variable-benefit-payment-step-form-group';
import { PortalService } from '../../../../../services/portal/portal.service';
import { PortalTypeIdEnum } from '../../../../../models/enum/shared/portal-type-id.enum';
import { DateConversionUtils } from '../../../../../utils/date-conversion-utils';
import { DateUtils } from '../../../../../utils/date-utils';

export interface AccountDetailItem {
  label: string;
  value: string;
  subtext?: string;
  tooltip?: string;
}

@Component({
  selector: 'app-variable-benefit-review-account-details',
  templateUrl: './variable-benefit-review-account-details.component.html',
  styleUrls: ['./variable-benefit-review-account-details.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class VariableBenefitReviewAccountDetailsComponent implements OnChanges {
  private portalService = inject(PortalService);

  @Input() configurationForm!: VariableBenefitConfigurationStepFormGroup;
  @Input() monthlyRecurringPaymentForm!: VariableBenefitRecurringPaymentFormGroup;
  @Input() annualRecurringPaymentForm!: VariableBenefitRecurringPaymentFormGroup;
  @Input() paymentForm!: VariableBenefitPaymentStepFormGroup;
  @Input() annualMinimumAmount!: number;
  @Input() isAnnualMinimumEstimated = false;
  @Input() usesEstimate = false;
  @Input() estimatedAnnualTotal: number | null = null;
  @Input() nextYearEstimatedAnnualTotal: number | null = null;

  accountDetails: AccountDetailItem[] = [];
  useEntireBalance: boolean = false;
  effectiveDateAnnotation: string = '';
  private effectiveYear = DateUtils.currentYearInCST();

  public trackByLabel(_: number, detail: AccountDetailItem): string {
    return detail.label;
  }

  ngOnChanges(_: SimpleChanges): void {
    this.portalService.portalTypeId$.once(portalTypeId => {
      this.updateAccountDetails(portalTypeId);
    });
  }

  private updateAccountDetails(portalTypeId: PortalTypeIdEnum): void {
    this.accountDetails = [];
    this.effectiveYear =
      DateConversionUtils.parseDateOnlyParts(this.configurationForm?.controls?.effectiveDate?.value ?? '')?.year ??
      DateUtils.currentYearInCST();
    this.useEntireBalance = this.configurationForm?.controls?.useEntireBalance?.value ?? false;
    this.effectiveDateAnnotation = this.useEntireBalance
      ? $localize`**Actual effective dates are subject to variation of up to five weeks from the set date.`
      : $localize`*Actual effective dates are subject to variation of up to five weeks from the set date.`;

    // Assets Used
    const desiredStartBalance = this.configurationForm?.controls?.desiredStartBalance?.value;
    if (desiredStartBalance !== null) {
      this.accountDetails.push({
        label: $localize`Assets Used`,
        value: this.formatCurrency(desiredStartBalance),
        subtext: this.useEntireBalance ? $localize`Entire balance used*` : undefined
      });
    }

    // Lump Sum Payment Date or Recurring Payments Starting In
    const lumpSumMonth = this.paymentForm?.controls?.lumpSumPaymentMonth?.value;
    const lumpSumYear = this.paymentForm?.controls?.lumpSumPaymentYear?.value;
    const forgoRecurring = this.paymentForm?.controls?.forgoRecurringPayments?.value;

    const monthlyMonth = this.monthlyRecurringPaymentForm?.controls?.paymentStartMonth?.value;
    const monthlyYear = this.monthlyRecurringPaymentForm?.controls?.paymentStartYear?.value;

    const annualMonth = this.annualRecurringPaymentForm?.controls?.paymentStartMonth?.value;
    const annualYear = this.annualRecurringPaymentForm?.controls?.paymentStartYear?.value;

    // Lump Sum
    if (lumpSumMonth && lumpSumYear && forgoRecurring) {
      this.accountDetails.push({
        label: $localize`Lump Sum Payment Date`,
        value: this.formatMonthYear(lumpSumMonth, lumpSumYear)
      });
    }
    // Recurring
    else if (!forgoRecurring && ((monthlyMonth && monthlyYear) || (annualMonth && annualYear))) {
      let selectedMonth!: number;
      let selectedYear!: number;

      if (monthlyMonth && monthlyYear && annualMonth && annualYear) {
        const monthlyValue = this.toMonthYearValue(monthlyMonth, monthlyYear);
        const annualValue = this.toMonthYearValue(annualMonth, annualYear);

        if (monthlyValue < annualValue) {
          selectedMonth = monthlyMonth;
          selectedYear = monthlyYear;
        } else {
          selectedMonth = annualMonth;
          selectedYear = annualYear;
        }
      } else if (monthlyMonth && monthlyYear) {
        selectedMonth = monthlyMonth;
        selectedYear = monthlyYear;
      } else {
        selectedMonth = annualMonth ?? 0;
        selectedYear = annualYear ?? 0;
      }

      this.accountDetails.push({
        label: $localize`Recurring Payments Starting In`,
        value: this.formatMonthYear(selectedMonth, selectedYear)
      });
    }

    // Estimated Annual Total
    this.accountDetails.push({
      label: $localize`Estimated Annual Total`,
      value: this.getEstimatedAnnualTotal(),
      tooltip: $localize`This figure reflects all payments made to date in the current year, combined with upcoming recurring and lump sum payments planned for this VB account.`
    });

    if (this.nextYearEstimatedAnnualTotal !== null) {
      this.accountDetails.push({
        label: $localize`Estimated ${this.effectiveYear + 1} Total`,
        value: this.formatCurrency(this.nextYearEstimatedAnnualTotal)
      });
    }

    // Effective Date
    const effectiveDate = this.configurationForm?.controls?.effectiveDate?.value;
    if (effectiveDate) {
      const labelString = this.useEntireBalance ? $localize`Effective Date**` : $localize`Effective Date*`;
      this.accountDetails.push({
        label: labelString,
        value: this.formatDate(effectiveDate)
      });
    }

    // Annual Minimum
    this.accountDetails.push({
      label: this.isAnnualMinimumEstimated ? $localize`Estimated Annual Minimum` : $localize`Annual Minimum`,
      value: this.formatCurrency(this.annualMinimumAmount),
      tooltip: this.getAnnualMinimumTooltip(portalTypeId)
    });

    // CRA Minimum Calculation Age
    const dateOfBirth = this.configurationForm?.controls?.minimumAgeDateOfBirth?.value;
    if (dateOfBirth) {
      const age = this.calculateAge(dateOfBirth);
      const isSpouse = this.configurationForm?.controls?.minimumAgeSpouseId?.value !== null;
      const memberLabel = isSpouse ? $localize`Spouse, Age ${age}` : $localize`Member, Age ${age}`;

      this.accountDetails.push({
        label: $localize`CRA Minimum Calculation Age`,
        value: this.formatDate(dateOfBirth),
        subtext: memberLabel
      });
    }
  }

  private formatCurrency(value: number): string {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(value);
  }

  private formatDate(dateString: string): string {
    const parts = DateConversionUtils.parseDateOnlyParts(dateString);
    if (!parts) {
      return '';
    }
    const date = new Date(Date.UTC(parts.year, parts.month - 1, parts.day));
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      timeZone: 'UTC'
    });
  }

  private formatMonthYear(month: number, year: number): string {
    const date = new Date(year, month - 1, 1);

    return date.toLocaleDateString('en-US', {
      month: 'long',
      year: 'numeric'
    });
  }

  private toMonthYearValue(month: number, year: number): number {
    return year * 12 + month;
  }

  private calculateAge(dateOfBirth: string): number {
    return DateConversionUtils.calculateAge(dateOfBirth);
  }

  private getAnnualMinimumTooltip(portalTypeId: PortalTypeIdEnum): string {
    if (this.isAnnualMinimumEstimated) {
      return $localize`The minimum total payment amount required for the year, based on estimates from the previous year for the variable benefit account balance(s) and the selected minimum age(s)`;
    } else if (portalTypeId === PortalTypeIdEnum.Member) {
      return $localize`Your required annual minimum payment, determined by your Variable Benefit Account's starting balance and minimum age selection.`;
    } else {
      return $localize`The minimum total payment amount required for the year, based on the Variable Benefit Account balance at the start of the year and the selected minimum age.`;
    }
  }

  private getMonthlyPaymentAmountForCurrentYear(): number {
    const startMonth = this.monthlyRecurringPaymentForm?.controls?.paymentStartMonth?.value;
    if (startMonth && this.monthlyRecurringPaymentForm?.controls?.paymentStartYear?.value === this.effectiveYear) {
      return (this.monthlyRecurringPaymentForm?.controls?.paymentAmount?.value ?? 0) * (13 - startMonth);
    } else {
      return 0;
    }
  }

  private getAnnualPaymentAmountForCurrentYear(): number {
    if (this.annualRecurringPaymentForm?.controls?.paymentStartYear?.value === this.effectiveYear) {
      return this.annualRecurringPaymentForm?.controls?.paymentAmount?.value ?? 0;
    } else {
      return 0;
    }
  }

  private getLumpSumPaymentAmountForCurrentYear(): number {
    const lumpSumYear = this.paymentForm?.controls?.lumpSumPaymentYear?.value;
    const forgoRecurring = this.paymentForm?.controls?.forgoRecurringPayments?.value;
    const desiredStartBalance = this.configurationForm?.controls?.desiredStartBalance?.value ?? 0;

    if (lumpSumYear === this.effectiveYear) {
      if (forgoRecurring) {
        return desiredStartBalance;
      } else {
        return this.paymentForm?.controls?.lumpSumPaymentAmount?.value ?? 0;
      }
    } else {
      return 0;
    }
  }

  private getEstimatedAnnualTotal(): string {
    if (this.usesEstimate) {
      return this.estimatedAnnualTotal === null ? '' : this.formatCurrency(this.estimatedAnnualTotal);
    }

    const total =
      this.getMonthlyPaymentAmountForCurrentYear() +
      this.getAnnualPaymentAmountForCurrentYear() +
      this.getLumpSumPaymentAmountForCurrentYear();

    return this.formatCurrency(total);
  }
}
